import { PageOrientation } from "@iventis/domain-model/model/pageOrientation";
import { PageSize } from "@iventis/domain-model/model/pageSize";
import { fontSizes, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { IVENTIS_LOGO } from "@iventis/utilities";
import React, { useMemo, useRef } from "react";

import { getDetailDimension, getSidebarLegendItemHeight, getSidebarLegendItemWidth } from "./export-helpers";

export const LegendComponent = ({
    orientation,
    pageSize,
    mapName,
    projectLogo,
    projectName,
    userName,
    remarks,
    sitemapsLevelName,
    bearing,
    scale,
}: {
    orientation: PageOrientation;
    pageSize: PageSize;
    mapName: string;
    projectName: string;
    projectLogo: string;
    userName: string;
    remarks: string;
    sitemapsLevelName: string;
    bearing: number;
    scale: string;
}) => {
    const layerContainerRef = useRef<HTMLDivElement>();
    const layers = Array.from(Array(20).keys());
    const detailsDimensions = useMemo(() => getDetailDimension(orientation, pageSize), [orientation, pageSize]);

    return (
        <Container orientation={orientation}>
            <LayerContainer detailsDimensions={detailsDimensions} orientation={orientation} pageSize={pageSize} ref={layerContainerRef}>
                {layers.map((layer) => (
                    <Layer orientation={orientation} pageSize={pageSize} key={layer} />
                ))}
            </LayerContainer>
            <DetailsContainer detailsDimensions={detailsDimensions} pageSize={pageSize} orientation={orientation} sitemapsLevelName={sitemapsLevelName}>
                <div className="grid-box full-width">
                    <span>{projectName}</span>
                </div>
                <div className="grid-box full-width">
                    <img src={projectLogo ?? IVENTIS_LOGO} className="project-image" alt="project-logo" data-testid="export-logo-preview" />
                </div>
                <div className="grid-box full-width">
                    <span data-testid="preview-title">{mapName}</span>
                </div>
                <div className="grid-box user-name">
                    <span>{userName}</span>
                </div>
                {sitemapsLevelName != null && (
                    <div className="grid-box half-width">
                        <span data-testid="sitemap-level-name">{sitemapsLevelName}</span>
                    </div>
                )}
                <div className="grid-box third-width right-border">
                    <span>{new Date().toLocaleDateString()}</span>
                </div>
                <div className="grid-box page-size third-width right-border" data-testid="map-export-legend-page-size">
                    {scale === "Custom" && <span>{pageSize}</span>}
                    {scale !== "Custom" && (
                        <span>
                            1:{scale} @ {pageSize}
                        </span>
                    )}
                </div>
                <div className="grid-box third-width rows">
                    <svg
                        data-testid="map-export-legend-compass"
                        aria-hidden="true"
                        focusable="false"
                        data-prefix="fas"
                        data-icon="dial"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 320 320"
                        color="#757575"
                        style={{ transform: `rotate(${360 - bearing}deg)`, height: "60%" }}
                    >
                        <path
                            fill="currentColor"
                            d="m0 0M.2157 159.3636c0-80.1859 58.9727-146.6539 135.9766-158.1798L136.263 159.3636c-0 13.2936 10.748 24.0416 23.9709 23.9709s24.0416-10.748 23.9709-23.9709l0-158.2505c76.9332 11.5966 135.9766 77.9939 135.9766 158.1798-0 88.3883-71.6299 160.0183-160.0183 160.0183s-160.0183-71.6299-160.0183-160.0183z"
                        />
                    </svg>
                    <span>NORTH</span>
                </div>
                <div className="grid-box full-width">
                    <span>Remarks</span>
                </div>
                <div className="remarks-text full-width">{remarks}</div>
            </DetailsContainer>
        </Container>
    );
};
const Container = styled.div<{ orientation: PageOrientation }>`
    display: flex;
    flex-direction: ${(props) => (props.orientation === PageOrientation.Landscape ? "column" : "row")};
    width: 100%;
    height: 100%;
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.two};
`;

const Layer = styled.div<{ orientation: PageOrientation; pageSize: PageSize }>`
    width: ${(props) => (props.orientation === PageOrientation.Landscape ? "80%" : `${getSidebarLegendItemWidth(props.pageSize)}%`)};
    height: ${(props) => getSidebarLegendItemHeight(props.pageSize)}px;
    min-height: ${(props) => getSidebarLegendItemHeight(props.pageSize)}px;
    background-color: ${({ theme }: { theme: Theme }) => theme.shades.three};
`;

const LayerContainer = styled.div<{ detailsDimensions: { height: number; width: number }; orientation: PageOrientation; pageSize: PageSize }>`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: column;

    padding: 5px;
    gap: ${(props) => getSidebarLegendItemHeight(props.pageSize)}px;
    flex-grow: 1;
    overflow: hidden;

    ${(props) =>
        props.orientation === PageOrientation.Portrait &&
        `
        max-width: calc(100% - ${(props) => props.detailsDimensions.width}%);
        flex-wrap: wrap;
    `}

    ${(props) =>
        props.orientation === PageOrientation.Landscape &&
        `
        max-height: calc(100% - ${(props) => props.detailsDimensions.height}%);

    `}
`;

const DetailsContainer = styled.div<{ detailsDimensions: { height: number; width: number }; pageSize: PageSize; orientation: PageOrientation; sitemapsLevelName: string }>`
    height: ${(props) => props.detailsDimensions.height}%;
    width: ${(props) => props.detailsDimensions.width}%;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
    grid-template-rows: repeat(6, 1fr);

    .project-image {
        width: calc(100% - 20px);
        height: auto;
        max-height: calc(100% - 5px);
        object-fit: contain;
    }

    .grid-box {
        overflow: hidden;
        border-top: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.two};
        font-size: ${(props) => (props.pageSize === PageSize.A4 ? fontSizes.xxSmall : props.pageSize === PageSize.A3 ? fontSizes.xxxSmall : fontSizes.xxxxSmall)};
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .grid-box:first-of-type {
        border-top: 0px;
    }

    .full-width {
        grid-column: span 6;
    }

    .half-width {
        grid-column: span 3;
    }
    .user-name {
        grid-column: span ${(props) => (props.sitemapsLevelName != null ? 3 : 6)};
        border-right: ${(props) => (props.sitemapsLevelName != null ? "1px" : "0px")} solid ${({ theme }: { theme: Theme }) => theme.shades.two};
    }

    .third-width {
        grid-column: span 2;
    }
    .right-border {
        border-right: 1px solid ${({ theme }: { theme: Theme }) => theme.shades.two};
    }

    .rows {
        display: flex;
        flex-direction: column;
    }

    .remarks {
        padding: 2px;
        align-items: flex-start;
        justify-content: flex-start;
        flex-direction: column;
    }

    ${(props) =>
        props.orientation === PageOrientation.Landscape &&
        `
            border-top: 1px solid ${props.theme.shades.two};
    `};

    ${(props) =>
        props.orientation === PageOrientation.Portrait &&
        `
            border-left: 1px solid ${props.theme.shades.two};
    `};
`;
